import { useState } from 'react'
import { SplitSection } from '@/components/marketing/section'
import { TextLink } from '@/components/marketing/text-link'

const coverage: { name: string; detail: string; href?: string }[] = [
  {
    name: 'Uptime',
    detail: 'HTTP, HTTPS, TCP, and Ping at 30-second intervals. Response-time percentiles and confirmation before alerting.',
    href: '/features/uptime-monitoring',
  },
  {
    name: 'Cron & heartbeat',
    detail: 'Your service pings a unique URL on schedule. Uptrack alerts if the ping is late or missing entirely. Included on every paid plan.',
    href: '/features/heartbeat-monitoring',
  },
  {
    name: 'SSL & DNS',
    detail: 'SSL expiry alerts before a certificate lapses. DNS record monitoring for A, AAAA, CNAME, MX, TXT, and NS.',
    href: '/features/ssl-monitoring',
  },
  {
    name: 'Status pages',
    detail: 'Public status page with custom domain support and subscriber notifications on paid plans. 1 on free, 5 on Pro; unlimited on Team and Business.',
    href: '/features/status-pages',
  },
]

const devTabs = [
  {
    id: 'api',
    label: 'API',
    description: 'Create a monitor in one request. Smart defaults handle the rest.',
    code: `curl -X POST https://api.uptrack.app/api/monitors \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{"url": "https://example.com"}'`,
    prefix: 'POST /api/monitors',
  },
  {
    id: 'cli',
    label: 'CLI',
    description: 'Monitors as code. Create, list, and manage from your terminal.',
    code: `# Install and run with npx (no global install needed)
npx @uptrack-app/cli create --url https://example.com
npx @uptrack-app/cli list
npx @uptrack-app/cli status`,
    prefix: 'npx @uptrack-app/cli',
  },
  {
    id: 'mcp',
    label: 'MCP',
    badge: 'New',
    description: 'Let Claude, ChatGPT, or Cursor manage your monitors directly.',
    code: `# Add to Claude Code (stdio transport)
claude mcp add uptrack -- npx -y @uptrack-app/mcp

# Or use HTTP transport directly
claude mcp add --transport http uptrack \\
  https://api.uptrack.app/mcp \\
  --header "Authorization: Bearer YOUR_API_KEY"`,
    prefix: 'npx @uptrack-app/mcp',
  },
] as const

export function MoreCoverage({ tone }: { tone?: 'default' | 'raised' }) {
  return (
    <SplitSection
      id="api"
      tone={tone}
      title="Everything else, from one dashboard"
      lead="Uptime, cron jobs, SSL certificates, DNS records, and public status pages. API, CLI, and AI assistant integration on every paid plan."
    >
      <ul className="grid grid-cols-1 gap-x-8 gap-y-7 sm:grid-cols-2">
        {coverage.map((c) => (
          <li key={c.name}>
            <h3 className="font-semibold text-foreground">{c.name}</h3>
            <p className="mt-1.5 text-[15px] leading-relaxed text-body">{c.detail}</p>
            {c.href && (
              <div className="mt-2">
                <TextLink to={c.href}>Details</TextLink>
              </div>
            )}
          </li>
        ))}
      </ul>
      <div className="mt-10 border-t border-border pt-8">
        <DevToolsTabs />
      </div>
    </SplitSection>
  )
}

function DevToolsTabs() {
  const [activeTab, setActiveTab] = useState<string>('api')
  const tab = devTabs.find((t) => t.id === activeTab) ?? devTabs[0]

  return (
    <div>
      <div role="tablist" className="flex gap-6 border-b border-border">
        {devTabs.map((t) => (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={activeTab === t.id}
            onClick={() => setActiveTab(t.id)}
            className={`-mb-px flex items-center gap-1.5 border-b-2 pb-2.5 text-sm font-medium transition-colors ${
              activeTab === t.id ? 'border-primary text-foreground' : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            {t.label}
            {'badge' in t && t.badge && <span className="text-xs font-normal text-primary">{t.badge}</span>}
          </button>
        ))}
      </div>
      <div className="mt-4 rounded-lg border border-border bg-background p-4">
        <div className="mb-2 font-mono text-xs text-muted-foreground">{tab.prefix}</div>
        <pre tabIndex={0} className="overflow-x-auto font-mono text-sm leading-relaxed text-foreground">
          <code>{tab.code}</code>
        </pre>
        <p className="mt-3 text-sm text-body">{tab.description}</p>
      </div>
    </div>
  )
}
